<template>
  <b-container fluid class="home-container">
    <b-row class="justify-content-between px-3">
      <h2 class="text-start asset-header ellipsis">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="24px"
          viewBox="0 0 24 24"
          width="24px"
          fill="#0096FF"
          :id="`popover-asset`"
        >
          <path d="M0 0h24v24H0z" fill="none" />
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
          />
        </svg>
        <b-popover :target="`popover-asset`" triggers="hover" placement="right">
          <template #title>Informations Platine</template>
          <p>Numéro de Série : {{ serialNumber }}</p>
          <p>Date de contrôle : {{ inspectionDate }}</p>
        </b-popover>
        {{ `Platine ${friendlyName}` }}
      </h2>
      <div>
        <span v-if="maintenance && !isMaintainer" class="flickering">
          Maintenance
        </span>
        <b-form-checkbox
          switch
          class="d-inline-block"
          v-model="maintenance"
          v-if="isMaintainer"
          @change="handleConfirmModal"
        >
          <span>Maintenance</span>
        </b-form-checkbox>
        <b-link class="pl-2 history" @click="navigateMaintenanceHistory">
          <img :src="ClockRorateLeft" width="16" height="16" />
          &nbsp;
        </b-link>
      </div>
    </b-row>
    <b-row v-for="(channel, index) of channels" :key="index">
      <b-col xl="5">
        <info-voie
          :name="channel.name"
          :maxH2="channel.metadata.maxH2"
          :packaging="channel.metadata.packaging"
          :size="channel.metadata.size"
          :capacity="channel.metadata.capacity"
          :status="channel.status"
          :measures="channel.measures"
          :timestamp="channel.updatedAt"
          :level="channel.level"
          :model="channel.metadata.model"
          :selected-frame-ref="channel.metadata.frameRef"
          :frame-reference="frameReference"
          @update-frame-reference="onUpdateFrameReference"
        />
      </b-col>
      <b-col xl="2" class="d-none d-xl-block">
        <info-cadre
          :level="channel.level"
          :pressure="channel.pressure"
          :model="channel.metadata.model"
          :maxH2="channel.metadata.maxH2"
          :packaging="channel.metadata.packaging"
          :size="channel.metadata.size"
          :capacity="channel.metadata.capacity"
          :channel-id="channel.channelId"
          :selected-frame-ref="channel.metadata.frameRef"
          :frame-reference="frameReference"
          @update-frame-reference="onUpdateFrameReference"
        />
      </b-col>
      <b-col xl="5">
        <line-chart
          :asset-id="channel.assetId"
          :channel-id="channel.channelId"
          :maxH2="channel.metadata.maxH2"
          :tenant="currentTenant.index"
        />
      </b-col>
    </b-row>
  </b-container>
</template>
<script>
import InfoVoie from '@/components/InfoVoie.vue';
import InfoCadre from '@/components/InfoCadre.vue';
import LineChart from '@/components/LineChart.vue';
import { mapGetters } from 'vuex';
import get from 'lodash/get';
import ClockRorateLeft from '@/assets/clock-rorate-left.svg';

export default {
  name: 'home',
  components: {
    InfoVoie,
    InfoCadre,
    LineChart
  },
  computed: {
    ...mapGetters('auth', [
      'currentLoggedinUser',
      'isMaintainer',
      'currentTenant'
    ])
  },
  async mounted() {
    await this.getPlatformFrameReference();
    await this.refresh(true);
    const tenant = this.currentTenant.index;
    await this.$kuzzle.realtime.subscribe(tenant, 'assets', {}, async () => {
      await this.refresh();
    });
  },
  data() {
    return {
      channels: [],
      maintenance: false,
      reference: null,
      friendlyName: null,
      serialNumber: null,
      inspectionDate: null,
      frameReference: [],
      frameChannelMapping: {
        channel1: 'frameChannel1',
        channel2: 'frameChannel2',
        channel3: 'frameChannel3',
        channel4: 'frameChannel4'
      },
      ClockRorateLeft
    };
  },
  methods: {
    async refresh(isMounted = false) {
      const { result: asset } = await this.$kuzzle.query({
          controller: "device-manager/assets",
          action: "get",
          _id: this.$route.params.assetId,
          engineId: this.currentTenant.index,
        });

      this.channels = [];

      this.maintenance = asset._source.metadata.maintenance

      for (const measure of Object.values(asset._source.measures)) {
        if (measure.type !== 'channel') {
          continue;
        }

        const frameName = this.frameChannelMapping[measure.name];

        const frame = asset._source.metadata[frameName];

        if (!frame) {
          continue;
        }

        if (!frame.maxH2 || !frame.reference) {
          continue;
        }

        const maxH2 = frame.maxH2;
        const frameRef = frame.reference;
        const volume = frame.volume;

        if (measure.values.temperature) {
          let level = maxH2 > 0 ? (get(measure.values, 'weight', 0) / maxH2) * 100 : 0;

          this.channels.push({
            channelId: measure.name,
            name: measure.name,
            assetId: asset._id,
            status: false,
            measures: {
              temperature: get(measure.values, 'temperature', 0) - 273.15,
              pression: get(measure.values, 'pressure', 0) / 100000,
              debit: get(measure.values, 'weight', null)
            },
            level: get(measure.values, 'level', level),
            metadata: {
              model: asset._source.model,
              maxH2,
              packaging: 'Cadre',
              size: 'V09',
              capacity: volume,
              frameRef: frameRef || 'Aucun'
            },
            updatedAt: get(asset._source, '_kuzzle_info.updatedAt', null)
          });
        } else {
          this.channels.push({
            channelId: measure.name,
            name: measure.name,
            assetId: asset._id,
            status: false,
            measures: {
              temperature: 'N/A',
              pression: 'N/A',
              debit: 'N/A'
            },
            level: 'N/A',
            metadata: {
              model: asset._source.model,
              maxH2,
              packaging: 'Cadre',
              size: 'V09',
              capacity: volume,
              frameRef: 'Aucun'
            }
          });
        }
        this.channels.sort((a, b) => a.name.localeCompare(b.name));
      }

      this.reference = asset._source.reference;
      const friendlyNameMetadata = asset._source.metadata.friendlyName;
      const serialNumberMetadata = asset._source.metadata.serialNumber;
      const inspectionDateMetadata = asset._source.metadata.inspectionDate;

      this.friendlyName = friendlyNameMetadata
        ? friendlyNameMetadata
        : asset._source.reference;
      this.serialNumber = serialNumberMetadata
        ? serialNumberMetadata
        : 'N/A';
      this.inspectionDate = inspectionDateMetadata
        ? inspectionDateMetadata
        : 'N/A';

      const activeChannel = asset._source.metadata.activeChannel;
      if (activeChannel) {
        this.channels.map(channel => {
          if (channel.channelId === activeChannel) {
            channel.status = true;
          }
        });
      }

      await this.$store.dispatch('asset/FETCH_CURRENT_ASSET', asset);
    },
    async handleConfirmModal(checked) {
      if (checked) {
        // confirm popup
        this.$bvModal
          .msgBoxConfirm('Confirmer le passage de ce cadre en maintenance ?', {
            cancelTitle: 'Annuler'
          })
          .then(async value => {
            // true: OK, false: CANCEL
            if (value) {
              await this.handleMaintenance(true);
            } else {
              this.maintenance = false;
            }
          });
      } else {
        // no need confirm popup
        await this.handleMaintenance(false);
      }
    },
    async handleMaintenance(checked) {
      this.maintenance = checked;
      const assetId = this.$route.params.assetId;

      await this.$kuzzle.query({
        controller: 'device-manager/assets',
        action: 'update',
        engineId: this.currentTenant.index,
        _id: assetId,
        body: {
          metadata: {
            maintenance: checked
          }
        }
      });
    },
    async fetchMeasure() {
      await this.$kuzzle.query({
        controller: 'byes/vestalis',
        action: 'fetch'
      });
    },
    async getPlatformFrameReference() {
      const sort = { volume: 'asc' };
      const result = await this.$kuzzle.document.search(
        'platform',
        'frame-reference',
        { sort }
      );

      if (result.total > 0) {
        for (const item of result.hits) {
          this.frameReference.push({
            id: item._id,
            name: item._source.name,
            volume: item._source.volume,
            maxH2: item._source.maxH2,
            weight: item._source.weight,
          });
        }
      }
    },
    async onUpdateFrameReference(channelId, data) {
      const assetId = this.$route.params.assetId;
      const tenant = this.currentTenant.index;
      const frameChannel = this.frameChannelMapping[channelId];

      await this.$kuzzle.query({
        controller: 'device-manager/assets',
        action: 'update',
        engineId: tenant,
        index: tenant,
        _id: assetId,
        body: {
          metadata: {
            [frameChannel]: {
              reference: data.name,
              volume: data.volume,
              maxH2: data.maxH2,
              weight: data.weight,
            }
          }
        }
      });
      await this.fetchMeasure();
      await this.refresh();
    },
    navigateMaintenanceHistory() {
      const frameId = this.$route.params.assetId;
      this.$router.push(`/maintenance-history/${frameId}`);
    }
  }
};
</script>

<style lang="scss" scoped>
.home-container {
  padding: 20px;
}

.asset-header {
  text-align: left;
  width: calc(100% - 165px);
}

.ellipsis {
  text-overflow: ellipsis;
  /* Required for text-overflow to do anything */
  white-space: nowrap;
  overflow: hidden;
}

.history {
  color: #4d4d4f;
  height: 24px;
}

.flickering {
  animation: flickering 1s infinite linear;
}

@keyframes flickering {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

@media (max-width: 767.98px) {
  .asset-header {
    font-size: 1.5rem;
  }
}
</style>
